// Shapes a flat `GET /orgs/{id}/folders` list (server/src/folders.rs) into what the sidebar
// shows: grouped by scope (Personal / Team / Org), nested to depth 3 under their parent, each
// scope's roots ordered the way the server already sorted them (created_at, id).
import type { FolderOut, FolderScope } from "../api/types";

export interface SidebarFolderItem {
  id: string;
  label: string;
  color: string;
  /** Nesting depth, 0 = top level of its scope. */
  depth: number;
  /** Set on the first item of a new scope group, e.g. "Personal". */
  scopeLabel?: string;
}

const SCOPE_ORDER: FolderScope[] = ["personal", "team", "org"];
const SCOPE_LABEL: Record<FolderScope, string> = {
  personal: "Personal",
  team: "Team",
  org: "Org",
};

const FOLDER_SWATCHES = ["purple", "ink", "clay", "teal"] as const;

/** Deterministic swatch per folder id, one of the DS folder colours (NavItem's swatch is a
 * background-color dot). */
export function folderColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return `var(--folder-${FOLDER_SWATCHES[Math.abs(hash) % FOLDER_SWATCHES.length]})`;
}

function flattenRoots(
  roots: FolderOut[],
  byParent: Map<string | null, FolderOut[]>,
  depth: number,
  out: SidebarFolderItem[],
): void {
  for (const folder of roots) {
    out.push({ id: folder.id, label: folder.name, color: folderColor(folder.id), depth });
    const children = byParent.get(folder.id);
    if (children && children.length > 0) {
      flattenRoots(children, byParent, depth + 1, out);
    }
  }
}

/** Builds the sidebar's flat, depth-annotated, scope-grouped folder list from the folders API's
 * flat response. Folders already come from the server ordered by (created_at, id) within each
 * parent, so grouping and nesting here never reorders siblings. */
export function buildSidebarFolders(folders: FolderOut[]): SidebarFolderItem[] {
  const out: SidebarFolderItem[] = [];
  for (const scope of SCOPE_ORDER) {
    const inScope = folders.filter((f) => f.scope === scope);
    if (inScope.length === 0) continue;

    const byParent = new Map<string | null, FolderOut[]>();
    for (const folder of inScope) {
      const list = byParent.get(folder.parent_id) ?? [];
      list.push(folder);
      byParent.set(folder.parent_id, list);
    }
    const roots = byParent.get(null) ?? [];
    const startIndex = out.length;
    flattenRoots(roots, byParent, 0, out);
    if (out.length > startIndex) {
      out[startIndex] = { ...out[startIndex], scopeLabel: SCOPE_LABEL[scope] };
    }
  }
  return out;
}

/** The folder tree for a "move to…" / "create subfolder" picker: same nesting as the sidebar,
 * but keeps the full `FolderOut` alongside the computed depth. */
export function flattenFoldersWithDepth(folders: FolderOut[]): { folder: FolderOut; depth: number }[] {
  const out: { folder: FolderOut; depth: number }[] = [];
  for (const scope of SCOPE_ORDER) {
    const inScope = folders.filter((f) => f.scope === scope);
    if (inScope.length === 0) continue;
    const byParent = new Map<string | null, FolderOut[]>();
    for (const folder of inScope) {
      const list = byParent.get(folder.parent_id) ?? [];
      list.push(folder);
      byParent.set(folder.parent_id, list);
    }
    const walk = (roots: FolderOut[], depth: number) => {
      for (const folder of roots) {
        out.push({ folder, depth });
        const children = byParent.get(folder.id);
        if (children && children.length > 0) walk(children, depth + 1);
      }
    };
    walk(byParent.get(null) ?? [], 0);
  }
  return out;
}
